import { useState } from 'react';
import { Link } from 'react-router';
import { Download, FileText } from 'lucide-react';
import {
  Badge,
  Button,
  DataTable,
  PageShell,
  Pagination,
  SectionCard,
  Select,
  Skeleton,
  toast,
  type DataTableColumn,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { useBusinessInquiryFiltersStore } from '@/stores/useBusinessInquiryFiltersStore';
import { BusinessInquiryFiltersBar } from '../components/BusinessInquiryFiltersBar';
import {
  downloadBusinessInquiriesExcel,
  useBusinessInquiriesQuery,
  useUnprocessedCountQuery,
} from '../hooks/useBusinessInquiries';
import {
  INQUIRY_MALL_SHORT,
  INQUIRY_STATUS_VARIANT,
  PAGE_SIZES,
  formatKst,
} from '../lib/businessInquiryLabels';
import type { BusinessInquirySummary } from '../types/businessInquiry';

/** QA-628 BO-01 비즈니스 문의 목록 — 고객센터 > 비즈니스 문의. No 최신순 고정. */
export const BusinessInquiryListPage = () => {
  const { filters, page, size, setPage, setSize } = useBusinessInquiryFiltersStore();
  const query = useBusinessInquiriesQuery(filters, page, size);
  const unprocessed = useUnprocessedCountQuery();
  const [exporting, setExporting] = useState(false);

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadBusinessInquiriesExcel(filters);
    } catch (e) {
      toast.error(friendlyMessage(e, '엑셀을 내려받지 못했습니다.'));
    } finally {
      setExporting(false);
    }
  };

  const columns: DataTableColumn<BusinessInquirySummary>[] = [
    { key: 'no', header: 'No', width: 70, align: 'center', cell: (r) => <span className="tabular-nums">{r.inquiryNo}</span> },
    { key: 'mall', header: '접수 몰', width: 70, align: 'center', cell: (r) => INQUIRY_MALL_SHORT[r.mall.code] ?? r.mall.label },
    { key: 'types', header: '문의 유형', width: 160, cell: (r) => r.types.map((t) => t.label).join(', ') },
    {
      key: 'title',
      header: '문의 제목',
      width: 280,
      // 제목이 길면 칸을 넓히지 않고 말줄임 — 전체 제목은 마우스를 올리면 보인다.
      cell: (r) => (
        <span className="flex max-w-[280px] items-center gap-1.5">
          <Link
            to={`/business-inquiries/${r.inquiryNo}`}
            className="min-w-0 truncate underline underline-offset-2"
            style={{ color: 'var(--content-strong)' }}
            title={r.title}
            data-testid="business-inquiry-title"
          >
            {r.title}
          </Link>
          {r.isNew ? (
            <Badge variant="danger" size="sm" data-testid="business-inquiry-new">
              NEW
            </Badge>
          ) : null}
        </span>
      ),
    },
    { key: 'companyName', header: '회사명', width: 140, cell: (r) => r.companyName || '—' },
    { key: 'name', header: '이름', width: 90, cell: (r) => r.name },
    {
      key: 'attachment',
      header: '첨부',
      width: 50,
      align: 'center',
      cell: (r) =>
        r.hasAttachment ? <FileText size={14} aria-label="사업자등록증 첨부" data-testid="business-inquiry-attachment" /> : '—',
    },
    {
      key: 'status',
      header: '처리 상태',
      width: 80,
      align: 'center',
      cell: (r) => (
        <Badge variant={INQUIRY_STATUS_VARIANT[r.status.code] ?? 'neutral'} size="sm">
          {r.status.label}
        </Badge>
      ),
    },
    { key: 'createdAt', header: '접수일시', width: 130, cell: (r) => <span className="tabular-nums">{formatKst(r.createdAt) ?? '—'}</span> },
  ];

  return (
    <PageShell title="비즈니스 문의" breadcrumb={[{ label: '고객센터' }, { label: '비즈니스 문의' }]}>
      <BusinessInquiryFiltersBar />

      <SectionCard padded={false}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-[13px]">
          <p data-testid="business-inquiry-summary">
            검색 결과 <strong className="tabular-nums">{(query.data?.totalItems ?? 0).toLocaleString()}</strong>건
            {unprocessed.data !== undefined ? (
              <>
                {' · '}미처리{' '}
                <strong className="tabular-nums" style={{ color: 'var(--status-danger-fg)' }}>
                  {unprocessed.data.toLocaleString()}
                </strong>
                건
              </>
            ) : null}
          </p>
          <div className="flex items-center gap-2">
            <Select
              className="w-32"
              value={String(size)}
              onChange={(e) => setSize(Number(e.target.value))}
              aria-label="목록 개수"
              data-testid="business-inquiry-size"
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}개씩 보기
                </option>
              ))}
            </Select>
            <Button type="button" variant="outline" size="sm" onClick={onExport} disabled={exporting} data-testid="business-inquiry-export">
              <Download size={14} aria-hidden="true" />
              엑셀 다운로드
            </Button>
          </div>
        </div>
        {query.isPending ? (
          <Skeleton className="m-4 h-24" />
        ) : query.isError ? (
          <p role="alert" className="p-4 text-[13px]" style={{ color: 'var(--status-danger-fg)' }}>
            {friendlyMessage(query.error, '비즈니스 문의 목록을 불러오지 못했습니다.')}
          </p>
        ) : (
          <>
            <DataTable
              className="whitespace-nowrap"
              columns={columns}
              rows={query.data.items}
              rowKey={(r) => r.inquiryNo}
              ariaLabel="비즈니스 문의 목록"
              emptyState="조건에 맞는 문의가 없습니다."
            />
            {query.data.totalItems > 0 ? (
              <div className="px-4 py-3">
                <Pagination total={query.data.totalItems} currentPage={page} pageSize={size} onPageChange={setPage} />
              </div>
            ) : null}
          </>
        )}
      </SectionCard>
    </PageShell>
  );
};
